// Mock data for Lockene certification & compliance landing page
// NOTE: This is frontend mock content. Lead capture is handled via the Lockene chat widget + WhatsApp/Call.

import {
  ShieldCheck, Radio, FileCheck2, Recycle, Scale, Stethoscope, BadgeCheck,
} from 'lucide-react';

export const company = {
  name: 'Lockene',
  tagline: 'Certification & Compliance Experts',
  phone: '+91-8668546474',
  phoneRaw: '918668546474',
  whatsapp: '918668546474',
  email: 'info@lockene.info',
  hours: 'Mon to Sat : 09:30 AM - 06:30 PM',
  logo: 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/x1lapz9g_Lockene%20Logo.png',
};

export const socials = [
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/lockene-compliance', icon: 'linkedin' },
  { name: 'YouTube', href: 'https://www.youtube.com/@lockeneCompliance', icon: 'youtube' },
  { name: 'Facebook', href: 'https://www.facebook.com/LockeneConsultant', icon: 'facebook' },
  { name: 'Instagram', href: 'https://www.instagram.com/lockeneconsultant/', icon: 'instagram' },
];

// WhatsApp deep link with an optional prefilled message
export const waLink = (msg) =>
  `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(
    msg || 'Hi Lockene, I would like to know more about your certification & compliance services.'
  )}`;

// Mailto link builder for the callback form
export const mailtoLink = ({ name, phone, service, message }) => {
  const subject = `New Callback Request${service ? ' - ' + service : ''}`;
  const body = [
    `Name: ${name || ''}`,
    `Phone: ${phone || ''}`,
    `Service: ${service || 'Not specified'}`,
    `Requirement: ${message || 'N/A'}`,
  ].join('\n');
  return `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

// Google Ads conversion tracking.
// TODO: Replace with the FULL conversion label from Google Ads, e.g. 'AW-18313043919/AbC-D_efGh12'
export const CONVERSION_SEND_TO = 'AW-18313043919';
export const fireAdsConversion = (eventId) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', { send_to: CONVERSION_SEND_TO });
    window.gtag('event', 'generate_lead');
  }
  // OpenAI Ads pixel conversion (appointment_scheduled). event_id enables
  // browser<->server deduplication with the Conversions API.
  if (typeof window !== 'undefined' && typeof window.oaiq === 'function') {
    const opts = eventId ? { event_id: eventId } : undefined;
    if (opts) {
      window.oaiq('measure', 'appointment_scheduled', { type: 'customer_action' }, opts);
    } else {
      window.oaiq('measure', 'appointment_scheduled', { type: 'customer_action' });
    }
  }
};

export const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'Why Us', href: '#why' },
  { label: 'Clients', href: '#clients' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Contact', href: '#contact' },
];

export const heroPoints = [
  'BIS / CRS Registration Consultant',
  'BIS ISI Certification Consultant',
  'CDSCO, WPC, TEC & Legal Metrology Experts',
];

export const serviceLogos = {
  bis: 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/hwvxo53g_BIS.png',
  isi: 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/wh52t502_ISI.png',
  tec: 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/san8d3ts_TEC.png',
  cdsco: 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/koan5g0e_CDSCO.png',
  'legal-metrology': 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/ehxkgyoz_Legal%20Metrology.png',
  wpc: 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/g57xuy84_WPC.png',
  trademark: 'https://customer-assets.emergentagent.com/job_lockene-styx/artifacts/kiy2ex4i_Trademark.png',
};

export const services = [
  {
    id: 'bis',
    icon: ShieldCheck,
    logo: serviceLogos.bis,
    logoDark: true,
    color: '#2563eb',
    title: 'BIS / CRS Certificate',
    short: 'Compulsory Registration Scheme for electronics & IT products.',
    desc: 'End-to-end BIS CRS support including documentation, product testing, application filing and BIS approval for Indian and international manufacturers.',
  },
  {
    id: 'isi',
    icon: BadgeCheck,
    logo: serviceLogos.isi,
    logoDark: true,
    color: '#0891b2',
    title: 'ISI Certificate',
    short: 'Mandatory ISI mark for products under compulsory BIS standards.',
    desc: 'Complete support for factory inspection, sample testing, documentation and fast-track ISI license approval to ensure regulatory compliance.',
  },
  {
    id: 'tec',
    icon: Radio,
    logo: serviceLogos.tec,
    color: '#7c3aed',
    title: 'TEC Certificate',
    short: 'Telecommunication Engineering Centre approvals for telecom gear.',
    desc: 'TEC (MTCTE) approvals for telecom products and equipment, ensuring compliance with Indian telecom regulatory standards.',
  },
  {
    id: 'cpcb',
    icon: Recycle,
    logo: null,
    color: '#16a34a',
    title: 'CPCB / EPR Certificate',
    short: 'Extended Producer Responsibility & pollution control compliance.',
    desc: 'EPR registration and CPCB compliance for e-waste, plastic and battery waste with eco-friendly recycling documentation support.',
  },
  {
    id: 'legal-metrology',
    icon: Scale,
    logo: serviceLogos['legal-metrology'],
    color: '#ea580c',
    title: 'Legal Metrology',
    short: 'Packaged commodity labelling & weights/measures compliance.',
    desc: 'LMPC compliance support for product labelling, packaging declarations and marking requirements as per Legal Metrology rules.',
  },
  {
    id: 'cdsco',
    icon: Stethoscope,
    logo: serviceLogos.cdsco,
    color: '#db2777',
    title: 'CDSCO Certificate',
    short: 'Medical devices & cosmetics import registration.',
    desc: 'CDSCO registration and import licenses for medical devices, cosmetics and drugs with complete regulatory documentation support.',
  },
  {
    id: 'wpc',
    icon: FileCheck2,
    logo: serviceLogos.wpc,
    logoDark: true,
    color: '#0d9488',
    title: 'WPC / ETA Certificate',
    short: 'Wireless Planning & Coordination equipment approvals.',
    desc: 'WPC ETA approvals ensuring compliance with Indian wireless and RF regulations for Bluetooth, Wi-Fi and other wireless devices.',
  },
  {
    id: 'trademark',
    icon: BadgeCheck,
    logo: serviceLogos.trademark,
    logoDark: true,
    color: '#0369a1',
    title: 'Trademark Registration',
    short: 'Protect your brand name, logo & identity legally.',
    desc: 'End-to-end trademark search, filing and registration support to protect your brand name, logo and identity across India.',
  },
];

export const detailedServices = [
  {
    id: 'bis-detail',
    title: 'BIS / CRS Registration \u2013 Fast & Hassle-Free Certification',
    body: 'BIS CRS (Compulsory Registration Scheme) is mandatory for manufacturers and importers of electronic and IT products. We offer end-to-end support including documentation, product testing, application filing and BIS approval. Our experienced team ensures a smooth, accurate and hassle-free certification process for both Indian and international manufacturers.',
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBtZWV0aW5nfGVufDB8fHx8MTc4MzQ5MTg5OHww&ixlib=rb-4.1.0&q=85',
    reverse: false,
  },
  {
    id: 'isi-detail',
    title: 'ISI Certification \u2013 License for Manufacturing',
    body: 'ISI Certification is mandatory for manufacturers producing products covered under compulsory BIS standards including wires, steel, cement, switches and appliances. We provide complete support for factory inspection, sample testing, documentation and fast-track ISI license approval\u2014enhancing customer confidence with authorized ISI marking.',
    image: 'https://images.pexels.com/photos/7731326/pexels-photo-7731326.jpeg',
    reverse: true,
  },
  {
    id: 'cdsco-detail',
    title: 'CDSCO Certification & Import Registration',
    body: 'From medical device import registration to cosmetics and drug licensing under CDSCO, our specialists manage the complete lifecycle\u2014technical documentation, coordination with authorities, inspections and approval\u2014so you stay fully compliant in the Indian market.',
    image: 'https://images.pexels.com/photos/6814524/pexels-photo-6814524.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    reverse: false,
  },
  {
    id: 'legal-metrology-detail',
    title: 'Legal Metrology (LMPC) Compliance',
    body: 'Legal Metrology registration is mandatory for importers and packers of pre-packaged commodities. We handle LMPC certificate filing, packaging declarations, labelling requirements and weights & measures compliance\u2014ensuring your products meet every statutory marking rule before they reach the market.',
    image: 'https://images.unsplash.com/photo-1612012060851-20f943c02d3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHx3ZWlnaGluZyUyMHNjYWxlfGVufDB8fHx8MTc4MzQ5NTk0Nnww&ixlib=rb-4.1.0&q=85',
    reverse: true,
  },
  {
    id: 'tec-detail',
    title: 'TEC (MTCTE) Certification for Telecom',
    body: 'TEC certification under the Mandatory Testing & Certification of Telecom Equipment (MTCTE) scheme is required for all telecom products sold in India. We provide end-to-end support covering documentation, lab testing coordination and final TEC approval\u2014ensuring seamless compliance with Indian telecom regulatory standards.',
    image: 'https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHx0ZWxlY29tJTIwZXF1aXBtZW50JTIwdGVzdGluZ3xlbnwwfHx8fDE3ODM0OTU5Mzl8MA&ixlib=rb-4.1.0&q=85',
    reverse: false,
  },
  {
    id: 'wpc-detail',
    title: 'WPC / ETA Approval for Wireless Devices',
    body: 'Any product using wireless technology\u2014Bluetooth, Wi-Fi, RFID or other RF modules\u2014requires WPC ETA approval before import or sale in India. Our team manages the complete ETA application, documentation and coordination to secure fast approvals in line with Indian wireless and RF regulations.',
    image: 'https://images.unsplash.com/photo-1537151377170-9c19a791bbea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHx3aXJlbGVzcyUyMGVsZWN0cm9uaWMlMjBkZXZpY2VzfGVufDB8fHx8MTc4MzQ5NTkzOXww&ixlib=rb-4.1.0&q=85',
    reverse: true,
  },
  {
    id: 'cpcb-detail',
    title: 'CPCB / EPR Environmental Compliance',
    body: 'Extended Producer Responsibility (EPR) registration with the CPCB is mandatory for producers, importers and brand owners handling e-waste, plastic and battery waste. We provide complete EPR registration, target compliance and annual return support\u2014keeping your business environmentally compliant and penalty-free.',
    image: 'https://images.unsplash.com/photo-1591193686104-fddba4d0e4d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxlLXdhc3RlJTIwcmVjeWNsaW5nfGVufDB8fHx8MTc4MzQ5NTkzOXww&ixlib=rb-4.1.0&q=85',
    reverse: false,
  },
];

export const stats = [
  { value: 18000, suffix: '+', label: 'Importers & Manufacturers Served' },
  { value: 5000, suffix: '+', label: 'Projects Completed' },
  { value: 40, suffix: '+', label: 'Expert Consultants' },
  { value: 15, suffix: '+', label: 'Years of Experience' },
];

export const whyPoints = [
  {
    title: '99% Approval Rate',
    desc: 'Trusted by global brands with 30\u201350% faster certification and near-perfect approvals.',
  },
  {
    title: 'Single-Window Solution',
    desc: 'ISI, BIS, WPC, CDSCO, Legal Metrology, EPR and more \u2014 all handled under one roof.',
  },
  {
    title: 'End-to-End Support',
    desc: 'Documentation, testing coordination and complete compliance support until approval.',
  },
  {
    title: 'Expert Consultants',
    desc: 'A dedicated team of 40+ specialists with 15+ years of domain expertise.',
  },
];

export const clients = [
  'https://styxinc.in/uploads/2024/09/Untitled-design-23.png',
  'https://styxinc.in/uploads/2024/09/Untitled-design-24.png',
  'https://styxinc.in/uploads/2024/09/3-1.png',
  'https://styxinc.in/uploads/2024/09/Untitled-design-43.png',
  'https://styxinc.in/uploads/2024/09/6.png',
  'https://styxinc.in/uploads/2024/09/5.png',
  'https://styxinc.in/uploads/2024/09/B-2.png',
  'https://styxinc.in/uploads/2024/09/11.png',
  'https://styxinc.in/uploads/2024/09/12.png',
  'https://styxinc.in/uploads/2024/09/B-3.png',
  'https://styxinc.in/uploads/2024/09/7.png',
  'https://styxinc.in/uploads/2024/09/15.png',
];

export const testimonials = [
  { name: 'Rajesh Kumar', role: 'Import Manager', text: 'Excellent service and professional support throughout our certification.', rating: 5 },
  { name: 'Anjali Mehra', role: 'Quality Head', text: 'Clear guidance and quick response from the team. Highly recommended.', rating: 5 },
  { name: 'Sushil Jore', role: 'Compliance Lead', text: 'Super fast service with on-time delivery! Lockene never disappoints.', rating: 5 },
  { name: 'Ravi Kiran', role: 'Operations Manager', text: 'No endless follow-ups \u2014 the ultimate solution for seamless compliance.', rating: 5 },
  { name: 'Pankaj Jhagwani', role: 'Director', text: 'Lockene has always been extremely helpful and easily accessible.', rating: 5 },
];

export const branches = [
  { type: 'HEAD OFFICE', city: 'NAGPUR', phone: '+91-8668546474', email: 'info@lockene.info' },
  { type: 'CORPORATE OFFICE', city: 'MUMBAI', phone: '+91 85303 85617', email: 'info@lockene.info' },
  { type: 'BRANCH OFFICE', city: 'DELHI', phone: '+91-9766656666', email: 'info@lockene.info' },
];

// On-Site Compliance Audits gallery (Lockene Compliance field work)
export const audits = [
  {
    location: 'GAMMA',
    place: 'Italy / Milan',
    image: 'https://styxinc.in/uploads/audits/gamma.jpeg',
    desc: 'Audit activities successfully conducted at GAMMA for furniture compliance and certification readiness. Our team reviewed applicable standards for work chairs, general-purpose chairs & stools, tables & desks, storage units and beds \u2014 ensuring every product aligns with Indian quality and safety requirements.',
  },
  {
    location: 'Turri',
    place: 'Italy / Milan',
    image: 'https://styxinc.in/uploads/audits/turri.jpeg',
    desc: 'A detailed pre-audit was carried out at Turri to evaluate adherence to applicable furniture standards for chairs, stools, tables, desks, storage units and beds \u2014 ensuring documentation, product quality and certification readiness are aligned before the final audit stages.',
  },
  {
    location: 'Rugiano',
    place: 'Italy / Milan',
    image: 'https://styxinc.in/uploads/audits/rugiano.jpeg',
    desc: 'Compliance pre-audit completed at Rugiano focusing on furniture category standards and production conformity. From chairs & stools to tables, storage units and beds, each product line was assessed for regulatory preparedness and quality assurance.',
  },
  {
    location: 'Apex Plastech',
    place: 'Thailand',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_lockene-styx/artifacts/2nxxrvbe_WhatsApp%20Image%202026-09-11%20at%201.33.31%20PM.jpeg',
    desc: 'Apex Plastech Co., Ltd. is a Thailand-based plastic fabrication company specializing in blow molding, offering custom services for the PET juice beverage bottle market \u2014 supported with end-to-end compliance readiness.',
  },
  {
    location: 'Grand Gain',
    place: 'Vietnam',
    image: 'https://styxinc.in/uploads/2025/03/Untitled-design-2025-03-24T161801.972.png',
    desc: 'Grand Gain Footwear Manufacturing Co., Ltd. specializes in manufacturing footwear and component parts, playing a key role in the supply chain for global sports shoe brands \u2014 audited for full certification conformity.',
  },
  {
    location: 'Samling Plywood',
    place: 'East Malaysia',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_lockene-styx/artifacts/yrev41bb_WhatsApp%20Image%202026-09-11%20at%201.28.50%20PM.jpeg',
    desc: 'Samling Plywood (Bintulu) Sdn. Bhd., headquartered in Bintulu, Malaysia, operates in the veneer, plywood and engineered wood product manufacturing sector \u2014 assessed for compliance with Indian import standards.',
  },
];
